import { Injectable } from "@angular/core";
import { Log, LoggingFactory, PlatformService, WindowRef } from "@obg/ngx.global.evo";
import { createInstance, LOCALSTORAGE } from "localforage";
import { isArray } from "lodash-es";

import { fromMinutesToMilliseconds } from "../utils/conversions/time-conversion";

import { CacheStore, GlobalCacheStore } from "./cache-store";
import { INDEXDB_KEYS_KEY, PREFIX_DEFAULT, PREFIX_LANGUAGE, PREFIX_VERSION } from "./cache.const";
import { CacheOptions, StorageMode } from "./cache.model";
import { isLocalStorageSupported } from "./cache.util";
import { memoryStorageDriver } from "./localforage/memory-driver";
const PREFIX_REGEX = new RegExp(".$");
const DEFAULT_CACHE_OPTIONS: CacheOptions = {
	isLanguageSpecific: false,
	isVersionSpecific: true,
	maxAge: fromMinutesToMilliseconds(5),
};

@Injectable({
	providedIn: "root",
})
export class CacheFactory {
	isLocalStorageAvailable: boolean;
	private logger: Log;

	constructor(
		private loggingFactory: LoggingFactory,
		private platformService: PlatformService,
		private windowRef: WindowRef
	) {
		this.logger = this.loggingFactory.get("cacheFactory");
		this.isLocalStorageAvailable = isLocalStorageSupported();
	}

	get(key: string, options?: CacheOptions): CacheStore {
		const cacheOptions: CacheOptions = Object.assign({}, DEFAULT_CACHE_OPTIONS, options);
		const cache = createInstance(this.getConfig(key, cacheOptions));
		// Warm up memory cache to prevent issues with cache readiness, probably a bug in the memory adapter
		if (options?.storageMode === StorageMode.Memory) {
			cache.ready();
		}
		return new GlobalCacheStore(cache, { maxAge: cacheOptions.maxAge });
	}

	getIndexDBKeys(): string[] {
		if (this.isLocalStorageAvailable) {
			const currentKeysValue = this.windowRef.native.localStorage.getItem(INDEXDB_KEYS_KEY) || "[]";
			return JSON.parse(currentKeysValue);
		}
		return []; // Return an empty array if localStorage is not available
	}

	private getConfig(key: string, options: CacheOptions) {
		const prefixedKey = `${this.generatePrefix(options)}${key}`;
		const name = options.skipPrefix ? key : prefixedKey;
		const storageMode = this.platformService.isServer ? StorageMode.Memory : options.storageMode;

		if (!storageMode && this.isLocalStorageAvailable) {
			const parsedKeys = this.getIndexDBKeys();
			const isKeyExisting = parsedKeys.find((parsedKey) => parsedKey === prefixedKey);

			if (!isKeyExisting && this.isLocalStorageAvailable) {
				const keys = isArray(parsedKeys) ? [...parsedKeys, prefixedKey] : [prefixedKey];
				this.windowRef.native.localStorage.setItem(INDEXDB_KEYS_KEY, JSON.stringify(keys));
			}
		}

		return storageMode
			? {
					name,
					driver: this.getStorageDriver(options),
				}
			: { name };
	}

	private generatePrefix(options: CacheOptions): string {
		const languagePrefix = options.isLanguageSpecific ? `${PREFIX_LANGUAGE}.` : "";
		const versionPrefix = options.isVersionSpecific ? `${PREFIX_VERSION}.` : "";

		return `${PREFIX_DEFAULT}.${languagePrefix}${versionPrefix}`.replace(PREFIX_REGEX, ":");
	}

	private getStorageDriver(options: CacheOptions): string {
		if (!this.isLocalStorageAvailable) {
			// fallback to memoryStorage if Browser in private mode
			return memoryStorageDriver._driver;
		}
		switch (options.storageMode) {
			case StorageMode.Memory:
				return memoryStorageDriver._driver;
			// todo: add support for indexedDBStorage
			case StorageMode.IndexedDBStorage:
				this.logger.warn(
					"getStorageDriver",
					"IndexedDBStorage not currently supported. Switching to localstorage driver."
				);
				return LOCALSTORAGE;
			default:
				return LOCALSTORAGE;
		}
	}
}
